import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { CircleNotch } from "@phosphor-icons/react";

async function fetchDialogRefsInto(setTypes, setUsers) {
  try {
    const [types, users] = await Promise.all([api.get("/reference/activity-types"), api.get("/users/options")]);
    setTypes(types.data);
    setUsers(users.data);
  } catch (e) {
    toast.error("Activity options could not be loaded. Please refresh.");
  }
}

const NONE = "__none__";
const today = () => new Date().toISOString().slice(0, 10);
const nowTime = () => new Date().toTimeString().slice(0, 5);

// Canonical activity outcomes — must match CANONICAL_OUTCOMES in server.py.
// New entries are restricted to this list so future data never needs repair.
export const CANONICAL_OUTCOMES = [
  "Connected", "Left voicemail", "No answer", "Email sent",
  "Meeting scheduled", "Meeting held", "Proposal sent", "Not interested",
];

// --- Extracted form/state helpers (structure-only refactor) ---
function buildInitialForm(activity, presetContactId, presetPropertyId) {
  const a = activity || {};
  return {
    activity_type_id: a.activity_type_id || "",
    activity_date: a.activity_date || today(),
    activity_time: a.activity_time || nowTime(),
    contact_id: a.contact_id || presetContactId || NONE,
    property_ids: a.property_ids?.length ? a.property_ids : (a.property_id || presetPropertyId ? [a.property_id || presetPropertyId] : []),
    subject: a.subject || "",
    notes: a.notes || "",
    outcome: a.outcome || "",
    next_action: a.next_action || "",
    follow_up_date: a.follow_up_date || "",
    follow_up_time: a.follow_up_time || "",
    follow_up_priority: a.follow_up_priority || "normal",
    owner_id: a.owner_id || "",
  };
}

function buildPayload(form, prospectId, includeOwner) {
  return {
    prospect_id: prospectId,
    activity_type_id: form.activity_type_id,
    activity_date: form.activity_date,
    activity_time: form.activity_time,
    contact_id: form.contact_id === NONE ? null : form.contact_id,
    property_id: form.property_ids[0] || null,
    property_ids: form.property_ids,
    subject: form.subject || null,
    notes: form.notes || null,
    outcome: form.outcome || null,
    next_action: form.next_action || null,
    follow_up_date: form.follow_up_date || null,
    follow_up_time: form.follow_up_time || null,
    follow_up_priority: form.follow_up_priority,
    owner_id: includeOwner && form.owner_id ? form.owner_id : undefined,
  };
}

// Mode: "complete" (Complete-and-Log), "edit", or "create"
async function saveActivity(mode, body, activity, completeFollowUpOf) {
  if (mode === "complete") {
    await api.post(`/activities/${completeFollowUpOf.id}/complete-follow-up`, { new_activity: body });
    return "Follow-up completed and new activity logged";
  }
  if (mode === "edit") {
    const { prospect_id, owner_id, ...updates } = body;
    await api.patch(`/activities/${activity.id}`, updates);
    return "Activity updated";
  }
  await api.post("/activities", body);
  return "Activity logged";
}

const DIALOG_TITLES = {
  complete: "Complete Follow-Up & Log Activity",
  edit: "Edit Activity",
  create: "Log Activity",
};
const SUBMIT_LABELS = { complete: "Complete & log", edit: "Save activity", create: "Log activity" };

function Fld({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}

// Log/edit an Activity. Parent remounts via key per target (no sync effects).
// completeFollowUpOf: when set, saving performs Complete-and-Log on that activity.
export default function ActivityDialog({ open, prospect, contacts, properties, activity, completeFollowUpOf, presetContactId, presetPropertyId, onClose, onDone }) {
  const { user, isAdmin } = useAuth();
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => buildInitialForm(activity, presetContactId, presetPropertyId));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const mode = completeFollowUpOf ? "complete" : activity ? "edit" : "create";

  const helpers = useRef({ fetchDialogRefsInto });
  const loadRefs = useCallback(() => helpers.current.fetchDialogRefsInto(setTypes, setUsers), [helpers, setTypes, setUsers]);
  useEffect(() => {
    loadRefs();
  }, [loadRefs]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = buildPayload(form, prospect.id, isAdmin);
      toast.success(await saveActivity(mode, body, activity, completeFollowUpOf));
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="activity-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">
            {DIALOG_TITLES[mode]}
          </DialogTitle>
          <DialogDescription>
            {mode === "complete"
              ? "The original follow-up stays on record and links to this new activity."
              : `For ${prospect.organization_name}. An outcome or notes entry is required.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Activity type" required>
              <Select value={form.activity_type_id} onValueChange={set("activity_type_id")} required>
                <SelectTrigger className="rounded-sm" data-testid="activity-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            {isAdmin && !activity && (
              <Fld label="Activity owner">
                <Select value={form.owner_id} onValueChange={set("owner_id")}>
                  <SelectTrigger className="rounded-sm" data-testid="activity-owner"><SelectValue placeholder={user?.name} /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Date" required><DatePicker value={form.activity_date} onChange={set("activity_date")} required testId="activity-date" /></Fld>
            <Fld label="Time" required><Input type="time" required value={form.activity_time} onChange={set("activity_time")} className="rounded-sm" data-testid="activity-time" /></Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Contact">
              <Select value={form.contact_id} onValueChange={set("contact_id")}>
                <SelectTrigger className="rounded-sm" data-testid="activity-contact"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {(contacts || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Related property / location (optional — the property or properties this activity concerns)">
              <div className="max-h-28 space-y-1 overflow-y-auto rounded-sm border border-[#E4E4E7] p-2" data-testid="activity-property">
                {(properties || []).length === 0 && <p className="text-xs text-[#6D6D70]">No properties on file</p>}
                {(properties || []).map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm text-[#52525B]">
                    <input type="checkbox" checked={form.property_ids.includes(p.id)}
                      onChange={(e) => set("property_ids")(e.target.checked
                        ? [...form.property_ids, p.id]
                        : form.property_ids.filter((id) => id !== p.id))}
                      data-testid={`activity-property-${p.id}`} />
                    {p.name}
                  </label>
                ))}
              </div>
            </Fld>
          </div>
          <Fld label="Subject / short description"><Input value={form.subject} onChange={set("subject")} className="rounded-sm" data-testid="activity-subject" /></Fld>
          <Fld label="Outcome">
            <Select value={form.outcome || NONE} onValueChange={(v) => set("outcome")(v === NONE ? "" : v)}>
              <SelectTrigger className="rounded-sm" data-testid="activity-outcome">
                <SelectValue placeholder="Select outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— No outcome yet —</SelectItem>
                {form.outcome && !CANONICAL_OUTCOMES.includes(form.outcome) && (
                  <SelectItem value={form.outcome}>{form.outcome} (legacy)</SelectItem>
                )}
                {CANONICAL_OUTCOMES.map((o) => <SelectItem key={o} value={o} data-testid={`outcome-option-${o}`}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Detailed notes"><Textarea value={form.notes} onChange={set("notes")} rows={3} className="rounded-sm" data-testid="activity-notes" /></Fld>
          <Fld label="Next action"><Input value={form.next_action} onChange={set("next_action")} className="rounded-sm" placeholder='e.g. "Send capability statement"' data-testid="activity-next-action" /></Fld>
          <div className="grid grid-cols-3 gap-3">
            <Fld label="Follow-up date"><DatePicker value={form.follow_up_date} onChange={set("follow_up_date")} testId="follow-up-date" /></Fld>
            <Fld label="Follow-up time"><Input type="time" value={form.follow_up_time} onChange={set("follow_up_time")} className="rounded-sm" data-testid="follow-up-time" /></Fld>
            <Fld label="Priority">
              <Select value={form.follow_up_priority} onValueChange={set("follow_up_priority")}>
                <SelectTrigger className="rounded-sm" data-testid="follow-up-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-activity">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : SUBMIT_LABELS[mode]}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
